import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Share2, ChevronRight, ExternalLink, ListOrdered, Loader } from 'lucide-react';

// Import AIO Components
import { AISnippetBlock, formatQuickAnswer, generateKeyPoints } from '@/components/AISnippetBlock';
import { PeopleAlsoAsk, DEFAULT_IPTV_FAQ } from '@/components/PeopleAlsoAsk';
import { BreadcrumbNav, createBlogBreadcrumbs } from '@/components/BreadcrumbNav';
import { SchemaGenerator } from '@/components/SchemaGenerator';
import { DEEP_LINKING_MAP, getRelatedArticles } from '@/components/BlogArticleContent';
import { getArticleImages, getOptimizedSearchQuery } from '@/services/imageService';
import { getCurrentMonthYear } from '@/services/eliteSummaryGenerator';
import { TableOfContents } from '@/components/TableOfContents';
import { MarkdownContentRenderer } from '@/components/MarkdownContentRenderer';
import { loadAllArticles } from '@/services/articleLoader';
import { ArticleTags } from '@/components/ArticleTags';
import { PillarHighlight } from '@/components/PillarHighlight';
import { RelatedByTags } from '@/components/RelatedByTags';
import { DynamicCTA } from '@/components/DynamicCTA';
import { GridBackground } from '@/components/GridBackground';
import { Footer } from '@/components/Footer';
import { ArticleFooter } from '@/components/ArticleFooter';
import { ScoreboardGrid } from '@/components/Sports/ScoreboardGrid';
import { NewsGrid } from '@/components/Sports/NewsGrid';
import '@/styles/eliteDesignSystem.css';

/**
 * StandardLayout - 2026 Google AI Overview Optimized Article Structure
 * 
 * Optimized for:
 * 1. Quick Answer (2-3 sentences) - Primary extraction target
 * 2. Expanded Answer (80-150 words) - Deeper context for AIO
 * 3. Step-by-step guidance (if applicable) - Lists for extraction
 * 4. FAQ block (5+ questions) - Follow-up AIO panels
 */
export const StandardLayout = ({ article }) => {
    if (!article) return null;

    // State for featured image
    const [featuredImage, setFeaturedImage] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [allArticles, setAllArticles] = useState([]);

    // Load all articles for related content (from /articles/ JSON)
    useEffect(() => {
        const loadArticles = async () => {
            try {
                const articles = await loadAllArticles();
                if (articles && articles.length > 0) {
                    setAllArticles(articles);
                }
            } catch (error) {
                console.warn('Could not load articles for related content:', error);
                setAllArticles([]);
            }
        };

        loadArticles();
    }, []);

    // Fetch featured image on mount or when article changes
    useEffect(() => {
        const fetchImage = async () => {
            try {
                setImageLoading(true);
                const searchQuery = getOptimizedSearchQuery(article.title);
                const images = await getArticleImages(searchQuery, 1);
                if (images.length > 0) {
                    setFeaturedImage(images[0]);
                }
            } catch (error) {
                console.error('Error fetching article image:', error);
            } finally {
                setImageLoading(false);
            }
        };

        fetchImage();
    }, [article.title, article.id]);

    // Generate AIO content - always use current month for freshness
    const quickAnswer = article.quickAnswer || formatQuickAnswer(article);
    const keyPoints = article.keyPoints || generateKeyPoints(article);
    const faqItems = article.faqItems || DEFAULT_IPTV_FAQ;
    const lastUpdated = getCurrentMonthYear();

    // Get related articles for internal linking
    const deepLinkedRelated =
        allArticles.length > 0 && getRelatedArticles
            ? getRelatedArticles(article.slug, allArticles)
            : [];

    const relatedArticles = deepLinkedRelated.length > 0
        ? deepLinkedRelated.slice(0, 4)
        : allArticles
            .filter(a => a.category === article.category && a.id !== article.id)
            .slice(0, 4);

    // Generate breadcrumbs
    const breadcrumbs = createBlogBreadcrumbs(article);

    // Check if article has steps (for How-To schema)
    const hasSteps = article.steps && article.steps.length > 0;

    // Prepare schema data
    const howToData = hasSteps ? {
        name: article.title,
        description: article.excerpt,
        totalTime: article.totalTime || 'PT10M',
        steps: article.steps

    } : null;

    // Detect sport context
    const sportContext = getSportContext(article.tags);

    return (
        <>
            {/* Schema Generator - All structured data */}
            <SchemaGenerator
                article={{
                    ...article,
                    datePublished: article.date,
                    dateModified: `2026-01-${String(Math.min(article.id + 10, 31)).padStart(2, '0')}`,
                    keywords: `${article.category}, iptv, best iptv service 2026, ${article.title.toLowerCase()}`
                }}
                faqItems={faqItems}
                breadcrumbs={breadcrumbs}
                howTo={howToData}
            />

            <div className="min-h-screen relative">
                {/* Main Gate Background */}
                <GridBackground />

                {/* Breadcrumb Navigation */}
                <div className="pt-4 px-4">
                    <div className="max-w-3xl mx-auto">
                        <BreadcrumbNav
                            items={breadcrumbs}
                            currentPageTitle={article.title}
                            showSchema={false}
                        />
                    </div>
                </div>

                {/* Article Header - Overview Section */}
                <section id="overview" className="py-8 px-4 border-b border-white/10">
                    <div className="max-w-3xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                                <span className="inline-block px-3 py-1 bg-primary/20 border border-primary/30 rounded-full text-xs font-semibold text-primary">
                                    {article.category}
                                </span>
                                <span className="inline-block px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-xs font-semibold text-green-400">
                                    ✓ Updated {lastUpdated}
                                </span>
                            </div>

                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                                {article.title}
                            </h1>

                            <div className="flex items-center justify-between text-muted-foreground mb-6 flex-wrap gap-4">
                                <div className="flex items-center gap-4 text-sm">
                                    <div className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        {article.readTime}
                                    </div>
                                    <span>{article.date}</span>
                                </div>
                                <button
                                    onClick={() => navigator.share?.({ title: article.title, url: window.location.href })}
                                    className="inline-flex items-center gap-2 text-primary hover:gap-3 transition text-sm"
                                >
                                    <Share2 className="w-4 h-4" /> Share
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Article Content with Sticky Sidebar Layout */}
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                        {/* Main Content Column (8 cols) */}
                        <main className="lg:col-span-8">
                            <motion.article
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <div id="quick-answer" className="mb-12">
                                    <AISnippetBlock
                                        quickAnswer={quickAnswer}
                                        keyPoints={keyPoints}
                                        lastUpdated={lastUpdated}
                                        variant="featured"
                                        content={article.content}
                                    />

                                </div>

                                {/* Smart Sports Injection (Contextual) */}
                                {sportContext && (
                                    <div className="mb-12">
                                        <div className="flex items-center gap-2 mb-6">
                                            <span className="relative flex h-3 w-3">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                            </span>
                                            <h3 className="text-xl font-bold text-white">Live {sportContext.label} Action</h3>
                                        </div>
                                        <div className="border border-white/10 rounded-xl overflow-hidden bg-black/20">
                                            {sportContext.type === 'f1' ? (
                                                <NewsGrid category="f1" />
                                            ) : (
                                                <ScoreboardGrid sport={sportContext.sport} league={sportContext.league} limit={3} />
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="glass rounded-xl p-8 mb-12">
                                    {imageLoading ? (
                                        <div className="mb-8 w-full aspect-video bg-white/5 rounded-lg flex items-center justify-center border border-white/10">
                                            <Loader className="w-6 h-6 text-primary animate-spin" />
                                        </div>
                                    ) : featuredImage ? (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                            className="mb-8"
                                        >
                                            <figure className="rounded-lg overflow-hidden border border-white/10">
                                                <img
                                                    src={featuredImage.url}
                                                    alt={featuredImage.alt || article.title}
                                                    className="w-full aspect-video object-cover"
                                                    loading="eager"
                                                    fetchPriority="high"
                                                />
                                                <figcaption className="py-2 px-4 bg-white/5 text-xs text-muted-foreground text-center">
                                                    Image by{' '}
                                                    <a
                                                        href={featuredImage.creditUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:underline"
                                                    >
                                                        {featuredImage.credit}
                                                    </a>
                                                    {' '}on{' '}
                                                    <span className="capitalize font-semibold">{featuredImage.source}</span>
                                                </figcaption>
                                            </figure>
                                        </motion.div>
                                    ) : null}

                                    <div className="max-w-none prose prose-invert prose-p:text-gray-300 prose-headings:text-white prose-a:text-primary hover:prose-a:text-primary/80">
                                        {article.expandedAnswer && (
                                            <div id="in-detail" className="elite-in-detail mb-12">
                                                <h3>📌 In Detail</h3>
                                                <p>{article.expandedAnswer}</p>
                                            </div>
                                        )}

                                        <div className="mb-12">
                                            <MarkdownContentRenderer content={article.content} />
                                        </div>

                                        {hasSteps && (
                                            <div id="step-by-step-guide" className="my-12">
                                                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                                                    <ListOrdered className="w-5 h-5 text-primary" />
                                                    Step-by-Step Guide
                                                </h2>
                                                <ol className="space-y-4">
                                                    {article.steps.map((step, index) => (
                                                        <li
                                                            id={`step-${index}`}
                                                            key={index}
                                                            className="flex gap-4 p-5 bg-white/5 rounded-lg border border-white/5 hover:border-primary/20 transition"
                                                        >
                                                            <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">
                                                                {index + 1}
                                                            </span>
                                                            <div>
                                                                <h3 className="font-semibold mb-1">{step.name}</h3>
                                                                <p className="text-sm text-muted-foreground">{step.text}</p>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                        )}

                                        {DEEP_LINKING_MAP[article.slug] && allArticles.length > 0 && (
                                            <div className="my-12 p-6 bg-white/5 border border-white/10 rounded-lg">
                                                <h3 className="text-lg font-semibold mb-4 text-white">Explore Related Topics</h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {DEEP_LINKING_MAP[article.slug].slice(0, 4).map((link, idx) => {
                                                        const linkedArticle = allArticles.find(a => a.slug === link.slug);
                                                        return linkedArticle ? (
                                                            <Link
                                                                key={idx}
                                                                to={`/blog/${link.slug}`}
                                                                className="text-primary hover:text-primary/80 transition text-sm font-medium flex items-center gap-2 group"
                                                            >
                                                                <span className="group-hover:translate-x-1 transition">→</span>
                                                                {link.anchor}
                                                            </Link>
                                                        ) : null;
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div id="people-also-ask" className="mt-12 pt-8 border-t border-white/10">
                                        <PeopleAlsoAsk
                                            faqItems={faqItems}
                                            title="People Also Ask About This Topic"
                                            variant="default"
                                        />
                                    </div>

                                    {/* Call to Action moved inside main card */}
                                    <div className="mt-12 p-6 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl">
                                        <h3 className="font-bold text-lg mb-2 text-white">Ready to Experience Premium IPTV?</h3>
                                        <p className="text-sm text-muted-foreground mb-6">
                                            Join 27,500+ satisfied customers streaming in 4K quality with 24/7 support.
                                        </p>
                                        <div className="flex gap-4 flex-wrap">
                                            <a
                                                href="https://xtreamcube.com/freetrialiptv/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-bold hover:bg-primary/90 transition shadow-lg shadow-primary/20"
                                            >
                                                Get Free 24H Trial <ExternalLink className="w-4 h-4" />
                                            </a>
                                            <Link
                                                to="/"
                                                className="inline-flex items-center gap-2 px-6 py-3 border border-primary/30 bg-primary/5 rounded-lg font-semibold hover:bg-primary/10 transition text-primary"
                                            >
                                                Learn More <ChevronRight className="w-4 h-4" />
                                            </Link>
                                        </div>
                                    </div>

                                    {/* Article Footer - Share & Navigation */}
                                    <ArticleFooter
                                        title={article.title}
                                        nextArticleTitle={relatedArticles[0]?.title || 'Best IPTV USA 2026'}
                                        nextArticleUrl={relatedArticles[0] ? `/blog/${relatedArticles[0].slug}` : '/blog/best-iptv-usa-2026'}
                                    />
                                </div>
                            </motion.article>
                        </main>

                        {/* Sticky Sidebar Column (4 cols) */}
                        <aside className="lg:col-span-4">
                            <div className="sticky top-24 space-y-5">

                                {/* 1. Table of Contents */}
                                <div className="glass rounded-xl p-6 border border-white/10">
                                    <TableOfContents article={article} />
                                </div>

                                {/* 2. Pillar Highlight (Context) */}
                                <PillarHighlight article={article} allArticles={allArticles} />

                                {/* 3. Article Tags */}
                                {article.tags && article.tags.length > 0 && (
                                    <div className="glass rounded-xl p-6 border border-white/10">
                                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                                            Topic Tags
                                        </h3>
                                        <ArticleTags tags={article.tags} variant="default" />
                                    </div>
                                )}

                                {/* 4. Share Actions */}
                                <div className="glass rounded-xl p-6 border border-white/10">
                                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                                        Share Article
                                    </h3>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => navigator.share?.({ title: article.title, url: window.location.href })}
                                            className="flex-1 flex items-center justify-center gap-2 p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition text-sm font-medium"
                                        >
                                            <Share2 className="w-4 h-4" /> Share
                                        </button>
                                        <Link
                                            to="/blog"
                                            className="flex-1 flex items-center justify-center gap-2 p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition text-sm font-medium"
                                        >
                                            all articles
                                        </Link>
                                    </div>
                                </div>

                                {/* 5. Dynamic CTA */}
                                <DynamicCTA article={article} variant="sidebar" />

                                {/* 6. Related By Tags (More From This Topic) */}
                                <RelatedByTags currentArticle={article} allArticles={allArticles} maxArticles={5} />

                            </div>
                        </aside>

                    </div>
                </div>

                {/* NOTE: ClusterLinks and RelatedSemantic are handled in BlogArticlePage.js to be full-width at bottom */}
            </div>
            {/* Footer moved to BlogArticlePage to appear AFTER ClusterLinks/RelatedSemantic */}
        </>
    );
};

/**
 * Helper: Prepare article for AIO template
 * Adds missing AIO fields with defaults
 */
export const prepareArticleForAIO = (article) => {
    return {
        ...article,
        quickAnswer: article.quickAnswer || formatQuickAnswer(article),
        keyPoints: article.keyPoints || generateKeyPoints(article),
        faqItems: article.faqItems || DEFAULT_IPTV_FAQ,
        lastUpdated: article.lastUpdated || 'January 2026',
        expandedAnswer: article.expandedAnswer || article.content?.substring(0, 400) + '...',
        steps: article.steps || null
    };
};

/**
 * Helper: Detect sport context from tags
 */
const getSportContext = (tags) => {
    if (!tags) return null;
    const lowerTags = tags.map(t => t.toLowerCase());

    if (lowerTags.includes('nba')) return { sport: 'basketball', league: 'nba', label: 'NBA' };
    if (lowerTags.includes('nfl')) return { sport: 'football', league: 'nfl', label: 'NFL' };
    if (lowerTags.includes('premier league') || lowerTags.includes('epl')) return { sport: 'soccer', league: 'eng.1', label: 'Premier League' };
    if (lowerTags.includes('la liga')) return { sport: 'soccer', league: 'esp.1', label: 'La Liga' };
    if (lowerTags.includes('formula 1') || lowerTags.includes('f1')) return { sport: 'racing', type: 'f1', label: 'Formula 1' };

    return null;
};
